import React, { FunctionComponent, ReactElement, useState, useEffect } from 'react';
import { FormattedMessage, useIntl, defineMessages, IntlShape } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import Modal from '@empiriecom/module-components/Modal';
import Image from '@empiriecom/module-components/Image';
import Headline from '@empiriecom/module-components/Headline';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import Price from '@empiriecom/module-components/Price';
import Notification from '@empiriecom/module-components/Notification';
import { useNotificationContext } from '@empiriecom/module-components/Notification/NotificationProvider';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { FragmentConfiguration } from '@/config';
import { useProduct } from '@/src/components/ProductContext';
import { useCreateRatingContext, CreateRatingStep } from './CreateRatingContext';
import CreateRatingForm from './CreateRatingForm';
import CreateRatingPreview from './CreateRatingPreview';
import CreateRatingTerms from './CreateRatingTerms';

const messages = defineMessages({
  ratingCreateHeadline: {
    defaultMessage: 'Bewertung verfassen',
    id: 'ratings.new-rating.modal-headline.create',
  },
  ratingPreviewHeadline: {
    defaultMessage: 'Bewertung überprüfen',
    id: 'ratings.new-rating.modal-headline.preview',
  },
  ratingTermsHeadline: {
    defaultMessage: 'Nutzungsbedingungen',
    id: 'ratings.new-rating.modal-headline.terms',
  },
});

const NewRatingWrapper = styled.div<{ mobile: boolean }>`
  margin: ${({ mobile }) => (mobile ? '0.75rem 0.75rem 0' : '0.75rem 1.5rem 0')};
  border: 1px dashed #c5c5c5;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0 1.75rem;
  font-size: 1rem;
  font-weight: 500;
  flex-direction: column;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  will-change: color;
  &&:hover {
    color: ${({ theme }) => theme.primary.color.normal};
  }
  svg {
    fill: #333;
    transition: fill 0.2s ease-in-out;
    will-change: fill;
  }
  &&:hover svg {
    fill: ${({ theme }) => theme.primary.color.normal};
  }
`;

const NewRatingIconWrapper = styled.div`
  background-color: #fff;
  border-radius: 50%;
  overflow: hidden;
  width: 4rem;
  height: 4rem;
  margin-bottom: 0.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContentWrapper = styled.div<{ mobile: boolean }>`
  padding: ${({ mobile }) => (mobile ? '1rem 0.75rem 2rem;' : '0.75rem 2rem 2rem;')};
`;

const ProductInformation = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ProductImage = styled(Image)`
  width: 4.5rem;
  height: 4.5rem;
  margin-right: 0.5rem;
`;

const ProductHeadline = styled.div`
  > h1 {
    font-size: 1rem;
    line-height: normal;
  }
`;

const NotificationWrapper = styled.div<{ mobile: boolean }>`
  margin: ${({ mobile }) => (mobile ? '0.75rem 0.75rem 0' : '0.75rem 1.5rem 0')};
`;

const determineModalHeadline = (
  creationStep: CreateRatingStep,
  setHeadline: (headline: string) => void,
  intl: IntlShape,
): void => {
  if (creationStep === 'TERMS') {
    setHeadline(intl.formatMessage(messages.ratingTermsHeadline));
    return;
  }
  if (creationStep === 'PREVIEW') {
    setHeadline(intl.formatMessage(messages.ratingPreviewHeadline));
    return;
  }
  setHeadline(intl.formatMessage(messages.ratingCreateHeadline));
};

/**
 * <p>This is the `CreateRating` component. It renders the create rating button and manages the
 * complete creation process via the `CreateRatingContext`.</p>
 * <p>Via click on the component the modal box containing the create rating form can be opened.</p>
 * <p>The terms and conditions can be opened by clicking the terms link within the terms input label.
 * A back button is provided on the terms view to get back to the create form.</p>
 * <p>If all required fields are filled in the "create preview button" will be activated. On errorneous
 * values error messages will be displayed accordingly.</p>
 * <p>If the "create preview button" is clicked a preview of the rating card will be shown containing all
 * the entered information given by the user. Also an "edit rating" button as well as a "submit rating" button
 * are provided on this view.</p>
 * <p>If the "edit rating" button is clicked the user gets back to the create rating form. The form will be filled
 * according to the previously entered data except for the terms checkbox. This one needs to be checked again manually
 * by the user.</p>
 * <p>If the "submit rating" button is clicked the rating data will be submitted to the system. If the submission has
 * been successful a message informing the user that it might take several days until the review is checked and
 * published will be displayed. On errorneous submissions a message informing the user that something went wrong will
 * be displayed.</p>
 * <p>If the modalbox is closed before the rating has been submitted all values will be reset. This also happens after the
 * rating has been submitted.</p>
 */
export const CreateRating: FunctionComponent = (): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [headline, setHeadline] = useState<string>();
  const { error, isLoading, product } = useProduct();
  const {
    price: { layout },
  } = useConfig<FragmentConfiguration>();
  const modalProps = {
    large: !isMobile,
  };
  const { resetRatingValues, getCreationStep } = useCreateRatingContext();
  const { pushNotification } = useNotificationContext();
  const intl = useIntl();
  const OpenIcon = getIcon('Plus');

  useEffect(() => {
    if (isModalOpen) {
      determineModalHeadline(getCreationStep(), setHeadline, intl);
    }
    if (getCreationStep() === 'SUBMIT_SUCCESS') {
      pushNotification({
        autoClose: 2500,
        content: (
          <FormattedMessage
            id="ratings.new-rating.submit-success-notification"
            defaultMessage="Bewertung erfolgreich abgeschickt. Vielen Dank!"
          />
        ),
        level: 'success',
      });
    } else if (getCreationStep() === 'SUBMIT_ERROR') {
      pushNotification({
        autoClose: 2500,
        content: (
          <FormattedMessage
            id="ratings.new-rating.submit-error-notification"
            defaultMessage="Bewertung konnte nicht abgeschickt werden!"
          />
        ),
        level: 'warning',
      });
    }
  }, [getCreationStep()]);

  return (
    <>
      {getCreationStep() !== 'SUBMIT_ERROR' && getCreationStep() !== 'SUBMIT_SUCCESS' && (
        <>
          <NewRatingWrapper
            mobile={isMobile}
            onClick={() => setIsModalOpen(true)}
            data-testid="create-rating"
          >
            <NewRatingIconWrapper>
              <OpenIcon size="1.875rem" />
            </NewRatingIconWrapper>
            <FormattedMessage
              id="ratings.new-rating.label"
              defaultMessage="Verfasse eine Bewertung"
            />
          </NewRatingWrapper>
          {isModalOpen && (
            <Modal
              testId="create-rating-modal"
              testIdClose="create-rating-modal-close"
              onRequestClose={() => {
                setIsModalOpen(false);
                resetRatingValues();
              }}
              isOpen={isModalOpen}
              headline={headline}
              noContentPadding
              // modal props need to be passed this way
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...modalProps}
            >
              <ModalContentWrapper mobile={isMobile}>
                <ProductInformation>
                  {error && null}
                  {isLoading && null}
                  {product && (
                    <>
                      <ProductImage
                        src={product.images[0].concat('?$format_dv_mob_075$')}
                        alt={product.name}
                        fallback={() => null}
                      />
                      <div>
                        <ProductHeadline>
                          <Headline level={1}>{product.name}</Headline>
                        </ProductHeadline>
                        <div>
                          <Price
                            amount={product.price.amount && product.price.amount}
                            currency={product.price.currency}
                            isMSRP={product.price.uvp && product.price.uvp}
                            isStartPrice={product.price.isStartPrice && product.price.isStartPrice}
                            layout={layout}
                            oldPrice={product.price.valueOld && product.price.valueOld}
                            price={product.price.value}
                            saving={product.price.saving && product.price.saving.value}
                            savingPrefix
                            savingType={
                              product.price.saving && product.price.saving.type
                                ? product.price.saving.type
                                : 'CURRENCY'
                            }
                            unit={product.price.unit && product.price.unit}
                            unitPrice={product.price.valueUnit && product.price.valueUnit}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </ProductInformation>
                {getCreationStep() === 'CREATE' && <CreateRatingForm />}
                {getCreationStep() === 'TERMS' && <CreateRatingTerms />}
                {getCreationStep() === 'PREVIEW' && (
                  <CreateRatingPreview setIsModalOpen={setIsModalOpen} />
                )}
              </ModalContentWrapper>
            </Modal>
          )}
        </>
      )}
      {getCreationStep() === 'SUBMIT_SUCCESS' && (
        <NotificationWrapper mobile={isMobile} data-testid="submit-rating-successful">
          <Notification
            isInline
            level="info"
            isAnimated={false}
            content={
              <FormattedMessage
                id="ratings.new-rating.submit-success"
                defaultMessage="Alle Bewertungen werden geprüft. In 3-5 Werktagen wird deine Bewertung für alle Kunden angezeigt."
              />
            }
          />
        </NotificationWrapper>
      )}
      {getCreationStep() === 'SUBMIT_ERROR' && (
        <NotificationWrapper mobile={isMobile} data-testid="submit-rating-error">
          <Notification
            isInline
            level="warning"
            isAnimated={false}
            onClose={() => resetRatingValues()}
            hasClose
            content={
              <FormattedMessage
                id="ratings.new-rating.submit-error"
                defaultMessage="Deine Bewertung konnte leider nicht übermittelt werden. Bitte versuche es erneut."
              />
            }
          />
        </NotificationWrapper>
      )}
    </>
  );
};

export default CreateRating;
